import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useNotification } from "../components/Notifications";
import { mapApiErrorToNotification } from "../utils/notificationHelpers";
import { apiFetch } from "../api/client";

export default function ResetPassword() {
  const [form, setForm] = useState({ email: "", otp: "", newPassword: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const notify = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    try {
      const qs = new URLSearchParams(location.search);
      const email = qs.get("email");
      if (email) setForm((f) => ({ ...f, email: decodeURIComponent(email) }));
    } catch (err) {
      // ignore
    }
  }, [location.search]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: null });
  };

  const submit = async (e) => {
    e.preventDefault();
    // client-side validation
    const nextErrors = {};
    if (!form.email) nextErrors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) nextErrors.email = "Invalid email format.";
    if (!form.otp) nextErrors.otp = "Token is required.";
    if (!form.newPassword) nextErrors.newPassword = "New password is required.";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      notify("error", { title: "Validation", text: "Please fix validation errors." });
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`/api/account/reset-password`, {
        skipTenant: true,
        method: "POST",
        body: JSON.stringify({ email: form.email, otp: form.otp, newPassword: form.newPassword }),
      });

      // stop spinner as soon as HTTP response is received so fields become editable
      setLoading(false);

      if (!res.ok) {
        let parsed = null;
        try {
          parsed = await res.json();
        } catch (err) {
          notify("error", { title: "Reset failed", text: res.statusText });
          setLoading(false);
          return;
        }

        const note = mapApiErrorToNotification(parsed, res.status);
        notify("error", note);
        setLoading(false);
        return;
      }

      const data = await res.json();
      if (data?.succeeded) {
        notify("success", { title: "Password reset", text: data.message || "Password successfully reset." });
        navigate("/login");
      } else {
        const note = mapApiErrorToNotification(data, res.status);
        notify("error", note);
      }
    } catch (err) {
      console.error(err);
      notify("error", { title: "Network error", text: "Could not reach server." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 h-screen items-center place-items-center">
        <div className="w-full max-w-md space-y-8 p-6 sm:p-10 rounded-lg">
          <div>
            <img className="mx-auto h-20 w-auto" src={require("../assets/stokraze.png")} alt="Your Company" />
            <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">Reset password</h2>
            <p className="mt-2 text-center text-sm text-gray-600">Enter the token we sent to your email and choose a new password.</p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={submit} noValidate>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email" className="sr-only">Email</label>
                <input id="email" name="email" type="email" autoComplete="email" required value={form.email} onChange={handleChange}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Email address" disabled={loading} />
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
              </div>
              <div className="mt-3">
                <label htmlFor="otp" className="sr-only">Token</label>
                <input id="otp" name="otp" type="text" required value={form.otp} onChange={handleChange}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Verification token" disabled={loading} />
                {errors.otp && <p className="mt-1 text-xs text-red-600">{errors.otp}</p>}
              </div>
              <div className="mt-3">
                <label htmlFor="newPassword" className="sr-only">New password</label>
                <input id="newPassword" name="newPassword" type="password" required value={form.newPassword} onChange={handleChange}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="New password" disabled={loading} />
                {errors.newPassword && <p className="mt-1 text-xs text-red-600">{errors.newPassword}</p>}
              </div>
            </div>

            <div>
              <button type="submit" disabled={loading}
                className="group relative flex items-center justify-center w-full py-3 px-4 border border-transparent text-base sm:text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                aria-busy={loading}
              >
                {loading && (
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                )}
                {loading ? "Resetting..." : "Reset password"}
              </button>
            </div>
          </form>
        </div>

        <div className="flex justify-center order-first sm:order-last p-4">
          {/* Mobile: small constrained image */}
          <img className="block sm:hidden max-w-full h-auto w-11/12 max-w-sm object-contain" src={require("../assets/SignUp2.png")} alt="" />

          {/* Desktop / laptop: use original natural size */}
          <img className="hidden sm:block" src={require("../assets/SignUp2.png")} alt="" />
        </div>
      </div>
    </>
  );
}
